import type { SortCallbackData } from '@shared/components/ui/Sort';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { usePacks } from '../../hooks/usePacks';
import { Pack } from '../../models/Pack';
import PacksGrid from './PacksGrid';
import PacksHeader from './PacksHeader';
import PacksModals, { PacksModalsRef } from './PacksModals';
import PacksStates from './PacksStates';

const Main: React.FC = () => {
  const { t } = useTranslation('packsModule');
  const modalsRef = useRef<PacksModalsRef>(null);
  const { canAccess } = usePermissions();
  // 🎯 Hook para manejar query parameters (para URL sync)
  const { params } = useQueryParams({
    search: { type: 'string', default: '' },
    sortBy: { type: 'string', default: 'created_at' },
    sortDirection: { type: 'string', default: 'desc' },
  });

  // 🎯 Estado local para filtros - esto es lo que realmente usa usePacks
  const [searchTerm, setSearchTerm] = useState(params.search);
  const [sortBy, setSortBy] = useState(params.sortBy);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(params.sortDirection as 'asc' | 'desc');

  // 🎯 Crear el objeto filters de forma que React detecte cambios
  const filters = useMemo(() => {
    const newFilters = {
      limit: 20,
      searchTerm: searchTerm ? searchTerm.trim() : undefined, // Solo enviar si hay término de búsqueda no vacío
      sortBy: sortBy, // 🎯 Campo para ordenar
      sortDirection: sortDirection, // 🎯 Dirección del ordenamiento
    };
    return newFilters;
  }, [searchTerm, sortBy, sortDirection]);

  const { packs, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage, refetch, total } = usePacks(filters);

  // 🎯 Hook para infinite scroll
  const { lastElementRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
    enabled: !isLoading && !error, // Solo habilitado si no hay errores ni carga inicial
  });

  // 🎯 Handlers para el header
  const handleCreatePack = useCallback(() => {
    modalsRef.current?.openCreateModal();
  }, []);

  const breadcrumbs = useMemo(() => [{ label: 'Packs', path: '/packs' }], []);

  const headerActions = useMemo(() => {
    const actions = [];

    // Verificar si el usuario puede crear packs
    const canCreate = canAccess(['tenant', 'tenant_3pl', 'tenant_external'], ['packs.create']);
    // ✅ Solo mostrar botón en header si hay packs y el usuario tiene permisos
    if (total > 0 && canCreate) {
      actions.push({
        id: 'create',
        label: t('actions.create_pack'),
        onClick: handleCreatePack,
        variant: 'primary' as const,
      });
    }

    return actions;
  }, [handleCreatePack, t, total, canAccess]);

  usePageNavigation({ breadcrumbs, headerActions });

  // 🎯 Handler para búsqueda - ahora actualiza el estado local
  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  // 🎯 Handler para ordenamiento - ahora actualiza el estado local
  const handleSort = useCallback((data: SortCallbackData) => {
    setSortBy(data.field);
    setSortDirection(data.direction);
  }, []);

  // 🎯 Handlers para los packs - ZERO re-renders
  const handleViewPack = useCallback((pack: Pack) => {
    modalsRef.current?.openViewModal(pack);
  }, []);

  const handleEditPack = useCallback((pack: Pack) => {
    modalsRef.current?.openEditModal(pack);
  }, []);

  const handleDeletePack = useCallback((pack: Pack) => {
    modalsRef.current?.openDeleteModal(pack);
  }, []);

  const handleLinkPack = useCallback((pack: Pack) => {
    modalsRef.current?.openLinkModal(pack);
  }, []);

  // 🎯 Handlers para estados
  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div className="px-6 my-4">
      {/* 🎯 Header - Filtros y botón crear */}
      <PacksHeader onSearch={handleSearch} onSort={handleSort} />

      {/* 🎯 Estados - Loading, Error, Empty */}
      <PacksStates
        isLoading={isLoading}
        error={error}
        packsCount={packs.length}
        searchTerm={searchTerm}
        onRefetch={handleRefetch}
        onCreatePack={handleCreatePack}
      />

      {/* 🎯 Grid - Packs con infinite scroll */}
      <PacksGrid
        packs={packs}
        error={error}
        isFetchingNextPage={isFetchingNextPage}
        lastElementRef={lastElementRef}
        onView={handleViewPack}
        onEdit={handleEditPack}
        onLink={handleLinkPack}
        onDelete={handleDeletePack}
      />

      {/* 🎯 Modales - Gestión centralizada */}
      <PacksModals ref={modalsRef} onRefetchPacks={refetch} />
    </div>
  );
};

export default Main;
